<script>
   /*************************************************************/
   /* AppPlots                                                  */
   /* -------------------------                                 */
   /* plots of the short data before and after preprocessing    */
   /*************************************************************/

   import { Container, Switch, Select } from 'svelte-controls-basic';
   import { Axes, Multilines, XAxis, YAxis, Box, Lines, Rectangles, TextLabels, ColormapLegend, getcolmap} from 'svelte-plots-basic/2d';
   import { text2svg } from 'svelte-plots-basic/utils';
   import { mrange, split } from 'mdatools/stat';
   import { Index } from 'mdatools/arrays';
   import { getObjColors } from '../../plots/Defaults.js';
   import { seq, escapeText } from '../../utils.js';
   import { plotSize, legendNames } from '../../plots/layout.js';

   let {
      rawdata,
      prepdata,
      varind,
      left,
      right,
      preview = false,  // the preprocessed set is the preview, the chain fitted to these rows alone
   } = $props();

   // prefix for the label ids the plot controls are named by
   const uid = $props.id();

   // plotting options
   let show = $state(true);         // if "true" shows plot for preprocessed data, otherwise for raw
   let colorBy = $state('none');    // how to color group lines
   let zoom = $state(false);        // zoom in or not x-axis

   // derived options
   const title = $derived((show ? 'Preprocessed' : 'Raw') + ' data (selected rows' +
      (show && preview ? ', preview' : '') + ')');
   const data = $derived(show && prepdata ? prepdata : rawdata);
   const label = $derived(data ? data.varaxisLabel : 'Variables');
   const units = $derived(rawdata.varaxisUnits);

   // the columns are cut when the bounds leave some out, and only for the preprocessed set, which
   // holds the predictors alone. 'left' is never below 0 in the creator and the dialog, so a test
   // on 0 cut an identity copy of the set whenever the bounds spanned it all
   const isTrimmed = $derived(left > 0 || (right > 0 && right < data.values.nrows));
   const indLeft = $derived(left >= 0 ? left + 1: 1);
   const indRight = $derived((right > 0 && right < data.values.nrows) ? right : data.values.nrows);
   const colInd = $derived(show && data && isTrimmed ? Index.seq(indLeft, indRight) : null);
   const xValues = $derived(colInd ? data.varvalues.filter((v, i) => i >= indLeft - 1 && i < indRight) : data.varvalues);
   const yValues = $derived(colInd ? data.values.subset(colInd, null) : data.values);

   // limX is based on original data range not on the trimmed values
   // limY will have extra 5% on top if color legend should be shown
   const yRange = $derived(yValues ? mrange(yValues.v) : null);
   const dY = $derived(yRange ? (yRange[1] - yRange[0]) * 0.05 : null);
   const limY = $derived(yRange ? [yRange[0], yRange[1] + (colorBy !== 'none') * dY] : [0, 1]);
   // the zoom applies while the left bound is above 0 and the right bound above it, the
   // condition that shows the switch. the switch keeps the user's choice, so the zoom is back
   // when the bounds return
   const canZoom = $derived(left > 0 && right > left);
   const zoomOn = $derived(zoom && canZoom);
   // the axis runs in the order of the variables, reversed for descending values as in the other
   // variable plots; the zoomed limits are in that order already
   const dir = $derived(data && data.varvalues.length > 1 &&
      data.varvalues[1] < data.varvalues[0] ? -1 : 1);
   const xRange = $derived(data ? mrange(data.varvalues, zoomOn ? 0.005 : 0.1) : [0, 1]);
   const limX = $derived(zoomOn && isTrimmed ?
      [data.varvalues[indLeft - 1], data.varvalues[indRight - 1]] :
      dir < 0 ? [xRange[1], xRange[0]] : xRange);

   // compute vector with colors
   const colors = $derived.by(() => {
      // one color - in fact will not be used
      const out = {'none': {'colors': '#ff0000'}};

      // color by groups if any
      if (rawdata.classes && rawdata.classNames && rawdata.classNames.length > 1) {
         const c = getcolmap(rawdata.classNames.length);
         out['groups'] = {
            colors: rawdata.classes.map(v => c[rawdata.classNames.findIndex(x => x === v)]),
            breaks: seq(0, rawdata.classNames.length),
            map: c
         }
      }

      // color by response if any
      if (rawdata.resp && rawdata.resp.length === rawdata.values.ncols) {
         const breaks = split(rawdata.resp, 8);
         const map = getcolmap(breaks.length - 1);
         out['response'] = {
            colors: getObjColors(rawdata.resp.v, breaks.v, map),
            labels: null,
            breaks,
            map
         }
      }

      return out;
   });

   // Axes' default margins, given to it explicitly as the legend names are fitted to them
   const margins = [1.0, 1.1, 0.55, 0.60];

   // the drawn size of the plot, for the group names cut to fit their legend cells
   let size = $state([0, 0]);
   const groupLabels = $derived(colors['groups'] ?
      legendNames(rawdata.classNames, size, margins, true) : null);
</script>

<div class="plot-panel">
{#if xValues && yValues}
   <!-- column with current plot -->
   <div class="plot-area">
      <div class="plot">
      <!-- 'display: contents' leaves the layout to Axes -->
      <div style="display: contents" {@attach plotSize(v => size = v)}>
      <Axes {title} {limY} {limX} {margins} fileName='plot-preprocessed' >

      <!-- all outcomes -->
      {#if colorBy === 'none'}
         <Multilines {xValues} {yValues} />
      {:else}
         {#each colors[colorBy].colors as col, i}
         <Lines {xValues} yValues={yValues.getcolumn(i + 1)} lineColor={col}/>
         {/each}
      {/if}

      <!-- vertical line for variable based normalization: the index is 0-based, so the first
           variable gets its marker too -->
      {#if varind && varind[0] >= 0 && varind[0] < data.varvalues.length}
      {@const x = data.varvalues[varind[0]]}
      <Lines xValues={[x, x]} yValues={limY} lineColor='#00000060'/>
      <TextLabels
         xValues={[x]}
         yValues={[limY[1] - dY * (colorBy === 'none' ? 1 : 2)]}
         labels={text2svg(x.toFixed(2) + ' ' + escapeText(units))}
         faceColor="#a0a0a0"
         textSize={0.8}
         pos={2}
      />
      {/if}

      <!-- rectangle to show trimming on the left side: bounded as the right one is. each rectangle
           starts at the edge drawn on the left and has a positive width; a bound past its own edge
           of the zoomed view, an earlier trim's or one moved wider before saving, draws none -->
      {#if varind && varind[1] > 0 && varind[1] < data.varvalues.length}
      {@const x = data.varvalues[varind[1]]}
      {@const w = (x - limX[0]) * dir}
      {#if w > 0}
      <Rectangles
         left={[limX[0]]}
         width={[w]}
         top={[limY[1]]}
         height={[limY[1] - limY[0]]}
         lineColor="transparent"
         faceColor="#00000040"
      />
      {/if}
      <TextLabels
         xValues={[x]}
         yValues={[limY[1] - dY * (colorBy === 'none' ? 1 : 2)]}
         labels={text2svg(x.toFixed(2) + ' ' + escapeText(units))}
         faceColor="#a0a0a0"
         textSize={0.8}
         pos={2}
      />
      {/if}

      <!-- rectangle to show trimming on the right side -->
      {#if varind && varind[2] < data.varvalues.length && varind[2] > 0}
      {@const x = data.varvalues[varind[2] - 1]}
      {@const w = (limX[1] - x) * dir}
      {#if w > 0}
      <Rectangles
         left={[x]}
         width={[w]}
         top={[limY[1]]}
         height={[limY[1] - limY[0]]}
         lineColor="transparent"
         faceColor="#00000040"
      />
      {/if}
      <TextLabels
         xValues={[x]}
         yValues={[limY[1] - dY * (colorBy === 'none' ? 1 : 2)]}
         labels={text2svg(x.toFixed(2) + ' ' + escapeText(units))}
         faceColor="#a0a0a0"
         textSize={0.8}
         pos={2}
      />
      {/if}

      <ColormapLegend show={colorBy !== 'none'} labels={colorBy === 'groups' ? groupLabels : colors[colorBy].labels}  breaks={colors[colorBy].breaks} colmap={colors[colorBy].map} />
      <XAxis label={escapeText(label)} showGrid={true} />
      <YAxis showGrid={true} />
      <Box />
   </Axes>
      </div>
      </div>
   </div>

   <!-- column with plot selectors and current plot settings -->
   <div class="plot-controls-area">
      <Container label=""><h2>Plot settings</h2></Container>
      <Container label="Show" id="{uid}-show">
         <Switch bind:value={show} options={["raw", "preprocessed"]} />
      </Container>
      {#if Object.keys(colors).length > 1}
      <Container label="Color by" id="{uid}-colorby">
         <Select bind:value={colorBy} options={Object.keys(colors)} />
      </Container>
      {/if}
      {#if canZoom}
      <Container label="Zoom (x-axis)" id="{uid}-zoom">
         <Switch bind:value={zoom} options={["off", "on"]} />
      </Container>
      {/if}
   </div>
{/if}
</div>

<style>

   .plot-panel {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      padding: 0;
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr minmax(270px, 30%);
   }

   .plot-area {
      padding: 1em;
      margin-top: 0.25rem;
      border-radius: 0.25em;
      background: #fefefe;
   }

   .plot-controls-area {
      padding: 0 0.25em 0 0.5em;
   }

   .plot {
      width: 100%;
      height: calc(100% - 15px);
   }


   @media(min-width:1100px) {
      .plot-panel {
         grid-template-columns: 1fr minmax(285px, 30%);
      }
   }

   @media(min-width:1300px) {
      .plot-panel {
         grid-template-columns: 1fr max(300px, 30%);
      }
   }

   @media(min-width:1600px) {
      .plot-panel {
         grid-template-columns: 1fr 400px;
      }
   }

</style>